import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Loader2, Building2, Truck, Info, Home } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState("supplier");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [consent, setConsent] = useState(false);
  const [form, setForm] = useState({
    email: "", password: "", name: "", phone: "", company_name: "", contact_person: "",
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) {
      setError("Please accept the POPIA consent to continue.");
      toast.error("Please accept the POPIA consent to continue.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const payload = { email: form.email, password: form.password, name: form.name, phone: form.phone, role };
      await register(payload);
      toast.success("Account created! A welcome email is on its way.");
      navigate("/dashboard");
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex h-16 max-w-4xl items-center justify-between px-4">
          <BrandLogo />
          <div className="flex items-center gap-2">
            <Link to="/">
              <Button variant="ghost" size="sm" className="gap-2 font-semibold" data-testid="home-button"><Home className="h-4 w-4" /> Home</Button>
            </Link>
            <Link to="/login">
              <Button variant="ghost" size="sm" className="font-semibold" data-testid="go-login-link">Sign in</Button>
            </Link>
          </div>
        </div>
      </header>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
        className="mx-auto max-w-2xl px-4 py-10">
        <h1 className="font-display text-3xl font-bold text-slate-900">Create your account</h1>
        <p className="mt-1 text-slate-500">Register as a supplier to bid, or as a buyer to post opportunities.</p>

        {/* Role toggle */}
        <div className="mt-6 grid grid-cols-2 gap-4">
          {[
            { v: "supplier", icon: Truck, title: "Supplier (SMME)", desc: "Bid on RFQs & tenders" },
            { v: "buyer", icon: Building2, title: "Buyer", desc: "Post RFQ / RFI / RFP / Tenders" },
          ].map(({ v, icon: Icon, title, desc }) => (
            <button key={v} type="button" onClick={() => setRole(v)} data-testid={`role-${v}-button`}
              className={`rounded-xl border p-5 text-left transition-all ${
                role === v ? "border-amber-500 bg-amber-50 ring-2 ring-amber-500/20" : "border-slate-200 bg-white hover:border-slate-300"
              }`}>
              <Icon className={`h-6 w-6 ${role === v ? "text-amber-600" : "text-slate-400"}`} />
              <div className="mt-2 font-display font-bold text-slate-900">{title}</div>
              <div className="text-sm text-slate-500">{desc}</div>
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="mt-8 space-y-8" data-testid="register-form">
          {/* Account */}
          <section className="rounded-xl border border-slate-200 bg-white p-6">
            <h3 className="font-display text-lg font-bold text-slate-900">Account details</h3>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div><Label>Contact full name *</Label><Input required value={form.name} onChange={set("name")} className="mt-1.5" data-testid="reg-name-input" /></div>
              <div><Label>Email *</Label><Input required type="email" value={form.email} onChange={set("email")} className="mt-1.5" data-testid="reg-email-input" /></div>
              <div><Label>Password *</Label><Input required type="password" minLength={6} value={form.password} onChange={set("password")} className="mt-1.5" data-testid="reg-password-input" /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} className="mt-1.5" data-testid="reg-phone-input" /></div>
            </div>
          </section>

          {/* Buyer-only company details */}
          {false && role === "buyer" && (
            <section className="rounded-xl border border-slate-200 bg-white p-6">
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div><Label>Company / department name *</Label><Input required value={form.company_name} onChange={set("company_name")} className="mt-1.5" data-testid="reg-company-input" /></div>
                <div><Label>Contact person</Label><Input value={form.contact_person} onChange={set("contact_person")} className="mt-1.5" data-testid="reg-contact-input" /></div>
              </div>
            </section>
          )}

          {/* Post-registration details note (supplier only) */}
          {role === "supplier" && (
            <div className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="details-note">
              <Info className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
              <p className="text-sm text-amber-900">
                Once your account is created, you'll complete your <strong>SMME Details</strong> (company information, BBBEE level, supply categories) and upload compliance documents from your dashboard. A welcome email will be sent to the address above.
              </p>
            </div>
          )}

          {error && <p className="text-sm font-medium text-red-600" data-testid="register-error">{error}</p>}

          <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-5" data-testid="popia-consent">
            <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5" data-testid="popia-consent-checkbox" />
            <span className="text-sm leading-relaxed text-slate-700">
              I consent to the processing of my business and personal information in accordance with the WonderStone Portal POPIA notice. I confirm that any information I submit is true and accurate.
            </span>
          </label>

          <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-6">
            <p className="text-sm text-slate-500">
              {role === "supplier" ? "Your application will be reviewed after you complete your SMME details." : "Your buyer account is activated immediately."}
            </p>
            <Button type="submit" disabled={loading || !consent} data-testid="register-submit-button" className="gap-2 bg-amber-600 px-6 py-6 font-semibold hover:bg-amber-700">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Create account
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
